import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { clearCoreSetupLocalStorage, clearLocalStorageByPrefix } from '../utils/localStorage';
import { logger } from '../utils/logger';

// Create component logger for localStorage cleanup hook
const cleanupLogger = logger.withComponent('LocalStorageCleanup');

export interface UseLocalStorageCleanupOptions {
  /** Patterns that trigger cleanup when route matches */
  triggerPatterns: string[];
  /** Custom prefix to clear (default: 'core_setup') */
  prefix?: string;
  /** Whether to clear on every route change (default: false) */
  clearOnEveryRoute?: boolean;
  /** Custom cleanup function */
  customCleanup?: () => void;
}

/**
 * Hook to automatically clear localStorage based on route changes
 * @param options - Configuration options for cleanup behavior
 */
export const useLocalStorageCleanup = (options: UseLocalStorageCleanupOptions) => {
  const location = useLocation();
  
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Check if current path matches any trigger patterns
    const shouldCleanup = options.clearOnEveryRoute || 
      options.triggerPatterns.some(pattern => {
        // Convert pattern to regex for flexible matching
        const regexPattern = pattern
          .replace(/:\w+/g, '[^/]+') // Replace :param with regex
          .replace(/\*/g, '.*'); // Replace * with regex
        
        const regex = new RegExp(`^${regexPattern}$`);
        return regex.test(currentPath);
      });
    
    if (shouldCleanup) {
      cleanupLogger.info('Route-based localStorage cleanup triggered', {
        currentPath,
        triggerPatterns: options.triggerPatterns,
        prefix: options.prefix || 'core_setup'
      });
      
      if (options.customCleanup) {
        options.customCleanup();
      } else if (options.prefix) {
        clearLocalStorageByPrefix(options.prefix);
      } else {
        clearCoreSetupLocalStorage();
      }
    }
  }, [location.pathname, options]);
};

/**
 * Pre-configured hook for QR route cleanup
 */
export const useQRRouteCleanup = () => {
  useLocalStorageCleanup({
    triggerPatterns: ['/qr/:sku/:serial'],
    prefix: 'core_setup'
  });
};
